import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom'
import { Flex, List, Modal, TextareaItem, WhiteSpace, WingBlank } from 'antd-mobile'
import { Button, Toast } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'
import { errorDialog } from '../../actions/index'
import OrderItem from './OrderItem'
import Coupon from './Coupon'
import {
  useGetInvoice,
  useTextbookOrder,
  useTextbookOrderCreate,
  useTextbookPaymentStatus,
  useTextbookCloseOrder,
} from '../../services/hooks'
import { isTaiwanUser } from '../../utils/user'
import { getWechatOpenId } from '../../utils/wechat'
import { getCookie } from '../../components/common/cookie'
import { isMainlandMobile, textbooksRegions } from '../../components/common/utils'
import {
  createWposPayTypePicker,
  FUIOU_H5_PAY_TYPE,
  isValidWposQrContent,
  pickFuiouChannel,
  safeExternalRedirect,
  safeFuiouRedirect,
} from '../../components/FormTools/utils'
import AgreementPopup from '../../components/FormTools/AgreementPopup'
import FuiouQrPopup from '../../components/FormTools/FuiouQrPopup'

import ShippingAddress from './ShippingAddress'
import PriceSummary from './PriceSummary'
import InvoiceInfo from './InvoiceInfo'

const findOrderPrice = (item, order) => {
  if (!order) return null
  const match = order.items.find((i) => item.goodsId === i.goodsId)
  return match ? match.discountPrice : null
}

// 订单备注限长：按 Unicode 码点算，与后端 com.padi.wechat.validation.@CodePointLength(max = 100)
// （value.codePointCount）同口径——emoji、生僻字各记 1 个字。
const REMARK_MAX_CODE_POINTS = 100
// 原生 maxlength 的兜底值：100 码点最坏情况是 200 个 UTF-16 码元（全是代理对），
// 故它永远不会先于下面的 clamp 触发。留着只是为了压掉 antd-mobile 自己算的动态上限
// （见 TextareaItem 处注释），不是为了限长。
const REMARK_NATIVE_MAX_LENGTH = REMARK_MAX_CODE_POINTS * 2

// Array.from 按码点迭代（emoji 记 1，不拆代理对），与后端 codePointCount 同口径
const clampToCodePoints = (text, max = REMARK_MAX_CODE_POINTS) => {
  const codePoints = Array.from(text)
  return codePoints.length > max ? codePoints.slice(0, max).join('') : text
}

const BooksCheckout = () => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const { state: orderState } = useLocation()
  const { items } = orderState || {}

  const [addressType, setAddressType] = useState()
  // 大陆省市（值为 PADI 参考接口原始 label，后端归一化后匹配运费档）
  const [province, setProvince] = useState()
  const [city, setCity] = useState()
  const [address, setAddress] = useState('')
  const [userName, setUserName] = useState('')
  const [userMobile, setUserMobile] = useState('')
  // 订单备注（选填，≤100 码点，CHNA-2753）：只随下单提交，不参与计价/改价重算
  const [remark, setRemark] = useState('')
  // 手机号格式校验的行内报错（显示在输入框下方），用户重新输入时清除
  const [mobileError, setMobileError] = useState('')
  // 收货表单各字段的行内报错（空值/未选择），改为输入框下方红字提示而非全局弹窗
  const [addressTypeError, setAddressTypeError] = useState('')
  const [provinceError, setProvinceError] = useState('')
  const [cityError, setCityError] = useState('')
  const [addressError, setAddressError] = useState('')
  const [userNameError, setUserNameError] = useState('')
  const [order, setOrder] = useState()
  const [couponVisiable, setCouponVisiable] = useState(false)
  const [selectedCoupon, setSelectedCoupon] = useState()
  const [invoiceInfo, setInvoiceInfo] = useState()
  const [visible, setVisible] = useState(false)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)
  // 合并为单个 state：React 16 无自动批处理，promise 回调里三连 setState 会触发三次渲染、
  // 连带 FuiouQrPopup 的 startPolling effect 多余重启
  const [qrState, setQrState] = useState({ url: '', visible: false, orderId: null, price: null, currency: null })
  // WPOS 渠道选择结果（WECHAT/ALIPAY），传给 FuiouQrPopup 展示对应渠道边框色与文案
  const [qrPayType, setQrPayType] = useState(null)
  // 渠道选择面板等待期间禁用下单按钮（loading 此时恒为 false）
  const [pickingChannel, setPickingChannel] = useState(false)
  const wposPickerRef = useRef(null)
  const { profile } = useSelector((state) => ({
    profile: state.data.profile,
  }))

  const history = useHistory()

  // items 缺失（history.state 丢失）时由下方 Redirect 兜底跳转，这里用空数组防渲染前崩溃。
  // useMemo 保持引用稳定：handlePaymentSuccess 依赖 freeItems，引用抖动会经 onSuccess →
  // pollStatus → startPolling 连锁，导致轮询在每个渲染周期被 stop/restart
  const freeItems = useMemo(
    () =>
      (items || []).map((item) => ({
        ...item,
        name: item.title,
        quantity: item.number,
      })),
    [items]
  )

  const freeData = {
    id: 'Free Order',
    items: freeItems,
    price: 0.0,
  }

  // 成功页收货信息：addressType 为地区枚举值，按当前语言映射为可读 label；大陆分支无 addressType，用省市展示
  const buildShippingInfo = useCallback(
    () => ({
      userName,
      userMobile,
      address,
      region:
        addressType != null
          ? (textbooksRegions[getCookie('language')] || []).find((region) => region.value === addressType)?.label
          : undefined,
      province,
      city,
    }),
    [addressType, userName, userMobile, address, province, city]
  )

  const { loading, run: submit } = useTextbookOrderCreate({
    // checkoutData 为提交时的完整载荷：备注必须取它而非当前 state——
    // 请求在途时用户仍可编辑备注框，state 可能已与后端实际收到的不一致
    onSuccess: (data, [checkoutData]) => {
      const { paymentMethodType, channel } = checkoutData || {}
      if (paymentMethodType === 'OFFLINE') {
        history.replace('/books_submit_success', { price: order.totalPayment })
      } else if (paymentMethodType === 'FUIOU' && channel === 'WPOS') {
        // PC WPOS 扫码分支：展示二维码 + 轮询支付状态。
        // 单码通道返回的是渠道二维码内容（支付宝 qr.alipay.com / 微信 weixin://），
        // 只做本地编码展示，校验用二维码内容白名单而非富友跳转域名白名单
        if (!isValidWposQrContent(data.url)) {
          Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-url-invalid' }) })
        } else if (!data.orderId) {
          // orderId 缺失无法轮询，直接提示失败，避免二维码永挂无反馈
          Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
        } else {
          // CHNA-2618：price 为订单实付总额（含快递费、已抵扣优惠券），currency 随通道变化
          setQrState({
            url: data.url,
            visible: true,
            orderId: data.orderId,
            price: data.price,
            currency: data.currency,
          })
        }
      } else if (paymentMethodType === 'FUIOU') {
        safeFuiouRedirect(data.url, formatMessage({ id: 'fuiou-pay-url-invalid' }))
      } else if (paymentMethodType && data.url) {
        // 海外 Stripe/TradeVan 等通道：非富友域名，跳转前校验 http(s) 协议
        safeExternalRedirect(data.url, formatMessage({ id: 'fuiou-pay-url-invalid' }), {
          // 白名单外域名弹确认（message 由 safeExternalRedirect 以 hostname 调用）
          message: (host) => formatMessage({ id: 'fuiou-external-redirect-confirm' }, { host }),
          confirm: formatMessage({ id: 'common-comfirmBtn' }),
          cancel: formatMessage({ id: 'common-cancel' }),
        })
      } else if (paymentMethodType === 'FREE') {
        // 免费订单：后端不返回支付链接，直接进成功页；附带币种/收货信息/备注供成功页展示
        history.replace('/pay_success', {
          payResult: {
            ...freeData,
            currencyType: freeItems[0] && freeItems[0].currencyType,
            shipping: buildShippingInfo(),
            remark: checkoutData.remark,
          },
        })
      } else {
        // 异常形态（无 paymentMethodType/无 url）：提示失败，不再伪装成"Free Order"成功页
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
      }
    },
  })

  const { run } = useTextbookOrder({
    onSuccess: (data) => {
      setOrder(data)
      const couponList = data.couponDTOS.map((coupon) => {
        coupon.selected = false
        return coupon
      })
      setSelectedCoupon(couponList)
    },
  })

  const { loading: invoiceLoading } = useGetInvoice({
    onSuccess: (data) => {
      setInvoiceInfo(data)
    },
  })

  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗）
  const { runAsync: checkPaymentStatus } = useTextbookPaymentStatus({ manual: true, onError: () => true })
  // 关单只由用户显式「取消订单」触发（弹窗关闭/超时都不再关单，见 FuiouQrPopup）：
  // 失败向上抛，由弹窗静默收起——后端幂等，失败时订单仍是 WAIT_PAY，可再次发起支付
  const { runAsync: closeTextbookOrder } = useTextbookCloseOrder({ manual: true, onError: () => true })
  const cancelOrder = useCallback(async () => {
    if (!qrState.orderId) return
    await closeTextbookOrder(qrState.orderId)
  }, [closeTextbookOrder, qrState.orderId])

  const checkStatus = useCallback(async () => {
    const result = await checkPaymentStatus(qrState.orderId)
    // hooks 层返回裸 body；已支付判定以订单状态为准——
    // paymentMethod 在订单创建时即写入（支付方式），不能作为支付完成信号。
    // TRADE_PAID（富友支付成功终态）与 PAID 等价：漏判会把已付款订单误判为未支付，
    // 诱导用户重复下单付款（二次扣款）
    return !!(result && ['PAID', 'TRADE_PAID', 'SHIPPED'].includes(result.status))
  }, [checkPaymentStatus, qrState.orderId])

  const handlePaymentSuccess = useCallback(() => {
    setQrState((prev) => ({ ...prev, visible: false }))
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      // 支付成功后不 reload 结算页：刷新后页面无任何"已支付"凭证，用户可能误以为未支付而
      // 重复下单付款。改为跳转通用支付成功页，携带真实订单号与商品清单（含备注），确认闭环成立
      afterClose: () =>
        history.replace('/pay_success', {
          payResult: {
            id: qrState.orderId || '-',
            items: freeItems,
            price: order ? order.totalPayment : 0,
            currencyType: freeItems[0] && freeItems[0].currencyType,
            shipping: buildShippingInfo(),
            remark: remark.trim(),
          },
        }),
    })
  }, [formatMessage, history, qrState.orderId, freeItems, order, buildShippingInfo, remark])

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  // 行内报错位于表单上部、提交按钮在下部：任一字段报错后自动滚动到第一个报错位置，
  // 避免红字出现在视口外用户看不见（报错 DOM 为 state 更新后渲染，需等下一帧再查）
  useEffect(() => {
    const errorKey = [addressTypeError, provinceError, cityError, addressError, userNameError, mobileError].join('|')
    if (!errorKey) return undefined
    const timer = setTimeout(() => {
      const firstError = document.querySelector('[class*="field-error"]')
      if (firstError) firstError.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 100)
    return () => clearTimeout(timer)
  }, [addressTypeError, provinceError, cityError, addressError, userNameError, mobileError])

  const checkout = () => {
    if (!order) return
    const isMainland = items && items.length > 0 && items[0].currencyType === 'CNY'
    if (isMainland) {
      // 大陆需先选省；市区是收货地址组成部分，所有省市一律必选（产品决策 2026-08-17，不特判内蒙古）
      if (!province) {
        setProvinceError(formatMessage({ id: 'addressForm-provinceHint' }))
        return
      }
      if (!city) {
        setCityError(formatMessage({ id: 'addressForm-cityHint' }))
        return
      }
      if (!address) {
        setAddressError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      if (!userName) {
        setUserNameError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      if (!userMobile) {
        setMobileError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      // 大陆订单手机号必须是 11 位大陆手机号（后端已限制，前端先行拦截），错误直接显示在输入框下方
      if (!isMainlandMobile(userMobile.trim())) {
        setMobileError(formatMessage({ id: 'addressForm-mobileHint' }))
        return
      }
    } else {
      if (!addressType) {
        setAddressTypeError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      if (!address) {
        setAddressError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      if (!userName) {
        setUserNameError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
      if (!userMobile) {
        setMobileError(formatMessage({ id: 'addressForm-saveHint' }))
        return
      }
    }
    if (profile.teachingLocation === 'TW' && !invoiceInfo) {
      dispatch(errorDialog('請填寫發票。'))
      return
    }
    if (order.totalPayment === 0) {
      submitCheckoutData('FREE')
      return
    }
    // 大陆订单走富友在线支付，不展示线下汇款选项（线下汇款为境外电汇账户，不适合大陆用户）
    if (isMainland) {
      verifyIpBeforePay(order.paymentMethodType)
      return
    }
    Modal.alert('', formatMessage({ id: 'payment-method' }), [
      { text: formatMessage({ id: 'online-pay' }), onPress: () => verifyIpBeforePay(order.paymentMethodType) },
      { text: formatMessage({ id: 'offline-pay' }), onPress: () => submitCheckoutData('OFFLINE') },
      { text: formatMessage({ id: 'common-cancel' }), onPress: () => {} },
    ])
  }

  const submitCheckoutData = async (paymentType) => {
    const channel = pickFuiouChannel()
    let payType = channel === 'H5PAY' ? FUIOU_H5_PAY_TYPE.WECHAT_JSAPI : undefined
    // 后端要求富友 WPOS 显式传 payType（FAPPLET 聚合码已下线）；OFFLINE/FREE/海外通道不需要
    if (paymentType === 'FUIOU' && channel === 'WPOS') {
      const picker = createWposPayTypePicker(formatMessage)
      wposPickerRef.current = picker
      setPickingChannel(true)
      try {
        payType = await picker.promise
      } finally {
        if (mountedRef.current) setPickingChannel(false)
        wposPickerRef.current = null
      }
      // 用户取消渠道选择，静默返回
      if (!payType) return
      if (mountedRef.current) setQrPayType(payType)
    }
    const checkoutData = {
      addressType,
      address,
      userName,
      userMobile,
      items,
      // 大陆省市（PADI 原始 label，后端归一化）；海外为 undefined，JSON 序列化自动剔除
      province,
      city,
      paymentMethodType: paymentType,
      channel,
      payType,
      openid: getWechatOpenId(),
      couponDTOS: selectedCoupon,
      // 订单备注（选填，≤100 码点）：输入框已按码点限长，此处 trim 后提交，空白备注后端存 NULL
      remark: remark.trim(),
    }

    if (invoiceInfo) {
      checkoutData.invoiceDTO = invoiceInfo
    }

    submit(checkoutData)
  }

  const verifyIpBeforePay = (paymentType) => {
    if (profile.teachingLocation === 'TW' || isTaiwanUser()) {
      setVisible(true)
    } else {
      submitCheckoutData(paymentType)
    }
  }

  useEffect(() => {
    const updateOrders = async () => {
      const isMainland = items && items.length > 0 && items[0].currencyType === 'CNY'
      if (isMainland) {
        // 大陆需先选省市（市区必选）才能预览运费
        if (!province || !city) return
        run({ items, province, city })
      } else {
        if (!addressType) return
        run({ addressType, items })
      }
    }

    updateOrders()
  }, [addressType, province, city, items, dispatch])

  const onRegionChange = (region) => {
    setAddressType(region)
    // TODO;
  }

  const getSelectedCouponCount = () => {
    return order.couponDTOS.filter((coupon) => coupon.selected).length
  }

  const toSelectCoupon = () => {
    if (order.couponDTOS.length === 0) return
    setCouponVisiable(true)
  }

  if (!items) return <Redirect to="/bookSaleList" />

  const currencyType = items[0].currencyType

  const getCoupon = (coupons) => {
    return coupons.filter((coupon) => !coupon.expired && !coupon.used)
  }

  return (
    <>
      <>
        <ShippingAddress
          address={address}
          setAddress={setAddress}
          addressType={addressType}
          onRegionChange={onRegionChange}
          userName={userName}
          userMobile={userMobile}
          setUserName={setUserName}
          setUserMobile={setUserMobile}
          mobileError={mobileError}
          setMobileError={setMobileError}
          addressTypeError={addressTypeError}
          setAddressTypeError={setAddressTypeError}
          provinceError={provinceError}
          setProvinceError={setProvinceError}
          cityError={cityError}
          setCityError={setCityError}
          addressError={addressError}
          setAddressError={setAddressError}
          userNameError={userNameError}
          setUserNameError={setUserNameError}
          isMainland={currencyType === 'CNY'}
          province={province}
          onProvinceChange={setProvince}
          city={city}
          onCityChange={setCity}
        />
        {!invoiceLoading && profile && profile.teachingLocation === 'TW' && <InvoiceInfo invoiceInfo={invoiceInfo} />}
        <List renderHeader={<FormattedMessage id="items-list" />} className="price-items">
          {items.map((item) => (
            <OrderItem key={item.goodsId} item={item} orderPrice={findOrderPrice(item, order)} />
          ))}
        </List>
        {/* 订单备注（选填，≤100 码点，CHNA-2753），限长由 clampToCodePoints 兜住，与后端
            @CodePointLength(max=100) 同口径。两种「省事」的写法都不行：
            · 只靠 maxLength —— 原生按 UTF-16 码元算，emoji 记 2，额度被砍半（100 个 emoji 只打进 50 个）；
            · 只靠 count —— antd-mobile 算出的原生上限在 iOS 上是「100 + 2×代理对数」，每输一个 emoji
              上限涨 2，等于对 emoji 完全不设限，能打过 100 码点再被后端 400（微信 H5 主要就是 iPhone）。
            故：clamp 是唯一真值，count 只负责计数显示（同为码点口径，不会出现「显示没满却打不进」），
            maxLength 只是个永不先触发的兜底。备注不参与计价，故不进优惠券重算的 submitData */}
        <List renderHeader={<FormattedMessage id="order-remark" />}>
          <TextareaItem
            placeholder={formatMessage({ id: 'order-remark-placeholder' })}
            value={remark}
            onChange={(value) => setRemark(clampToCodePoints(value))}
            maxLength={REMARK_NATIVE_MAX_LENGTH}
            rows={2}
            count={REMARK_MAX_CODE_POINTS}
            autoHeight
          />
        </List>
        <WhiteSpace />
        <WhiteSpace />
        {order && (
          <List>
            <List.Item
              extra={
                getCoupon(order.couponDTOS).length ? (
                  <span style={{ color: getSelectedCouponCount() > 0 ? '#000' : '#aaa' }}>
                    {getSelectedCouponCount() > 0 ? (
                      <FormattedMessage
                        id="coupon-used"
                        values={{
                          count: getSelectedCouponCount(),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="has-coupon"
                        values={{
                          count: getCoupon(order.couponDTOS).length,
                        }}
                      />
                    )}
                  </span>
                ) : (
                  <span>
                    <FormattedMessage id="coupon-without" />
                  </span>
                )
              }
              arrow="horizontal"
              onClick={toSelectCoupon}
            >
              <span>
                <FormattedMessage id="coupon" />
              </span>
            </List.Item>
          </List>
        )}
        <PriceSummary selectedCoupon={selectedCoupon} currencyType={currencyType} order={order} />
        <WhiteSpace />
        <WhiteSpace />
        <WingBlank>
          <Button
            color="primary"
            block
            onClick={checkout}
            loading={loading}
            disabled={!order || loading || pickingChannel}
          >
            <FormattedMessage id="checkout" />
          </Button>
          <WhiteSpace />
          <Flex justify="center">
            <Link
              replace
              to={{
                pathname: '/bookSaleList',
                state: { items },
              }}
            >
              <FormattedMessage id="change-order" />
            </Link>
          </Flex>
        </WingBlank>
      </>
      {couponVisiable && (
        <Coupon
          selectedCoupon={selectedCoupon}
          setSelectedCoupon={setSelectedCoupon}
          setVisiable={setCouponVisiable}
          setOrder={setOrder}
          submitData={{
            addressType,
            address,
            userName,
            userMobile,
            items,
            // 大陆订单运费依赖省市（与主预览 run({ items, province, city }) 同参数形态）：
            // 缺省市时用券重算会按缺省运费返回错误总价，极端下 totalPayment 为 0 误触发 FREE 下单。
            // POST 序列化自动丢弃 undefined，非大陆订单不受影响
            province,
            city,
          }}
        />
      )}
      <AgreementPopup
        visible={visible}
        setVisible={setVisible}
        toPay={() => submitCheckoutData(order.paymentMethodType)}
      />
      <FuiouQrPopup
        visible={qrState.visible}
        url={qrState.url}
        price={qrState.price}
        currency={qrState.currency}
        onClose={() => setQrState((prev) => ({ ...prev, visible: false }))}
        onPaymentSuccess={handlePaymentSuccess}
        checkStatus={checkStatus}
        payType={qrPayType}
        onCancelOrder={cancelOrder}
      />
    </>
  )
}

export default BooksCheckout
